import { useEffect, useState } from "react";
import { GitCompare, Lock, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { fmtDateTime, label } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { Card, Field } from "../common/UI";
import { Button } from "../ui/button";

function Editor({ m, current, onClose, onSaved }) {
  const [f, setF] = useState({ title: current?.title || m.proposed_title, whereas_clauses: current?.whereas_clauses || "", enacting_clause: current?.enacting_clause || "", body: current?.body || "", change_summary: "" });
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try {
      await api.post(`/measures/${m.id}/versions`, { ...f, row_version: m.row_version });
      toast.success("New version saved");
      onClose();
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Card title="Prepare a new version" testId="version-editor">
      <div className="space-y-3">
        <Field label="Title"><textarea rows={2} className="otms-input" value={f.title} onChange={up("title")} data-testid="version-title-input" /></Field>
        <Field label="Whereas clauses"><textarea rows={4} className="otms-input font-mono text-xs" value={f.whereas_clauses} onChange={up("whereas_clauses")} data-testid="version-whereas-input" /></Field>
        <Field label="Enacting / resolving clause"><textarea rows={2} className="otms-input font-mono text-xs" value={f.enacting_clause} onChange={up("enacting_clause")} /></Field>
        <Field label="Body"><textarea rows={12} className="otms-input font-mono text-xs" value={f.body} onChange={up("body")} data-testid="version-body-input" /></Field>
        <Field label="Summary of changes *"><input className="otms-input" value={f.change_summary} onChange={up("change_summary")} data-testid="version-summary-input" /></Field>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-sky-600 hover:bg-sky-700" disabled={f.change_summary.trim().length < 3} onClick={save} data-testid="version-save-button">Save as new version</Button>
        </div>
      </div>
    </Card>
  );
}

function Compare({ m, versions }) {
  const [a, setA] = useState(versions[1]?.id || "");
  const [b, setB] = useState(versions[0]?.id || "");
  const [res, setRes] = useState(null);
  const run = () => api.get(`/measures/${m.id}/versions/compare`, { params: { a, b } }).then((r) => setRes(r.data)).catch((e) => toast.error(errMsg(e)));
  const opt = versions.map((v) => <option key={v.id} value={v.id}>v{v.version_no} — {v.change_summary}</option>);
  return (
    <Card title="Compare versions" testId="version-compare">
      <div className="flex flex-wrap items-end gap-2">
        <select className="otms-input w-60" value={a} onChange={(e) => setA(e.target.value)} data-testid="compare-a-select">{opt}</select>
        <span className="pb-2 text-xs text-slate-400">→</span>
        <select className="otms-input w-60" value={b} onChange={(e) => setB(e.target.value)} data-testid="compare-b-select">{opt}</select>
        <Button size="sm" variant="outline" onClick={run} disabled={!a || !b} data-testid="compare-run-button"><GitCompare className="mr-1.5 h-4 w-4" />Compare</Button>
      </div>
      {res && (
        <div className="mt-4 space-y-4" data-testid="compare-result">
          {res.fields.map((f) => (
            <div key={f.field}>
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label(f.field)}</div>
              <div className="whitespace-pre-wrap rounded border border-slate-200 bg-slate-50 p-3 font-mono text-xs leading-relaxed">
                {f.ops.map((o, i) => <span key={i} className={o.type === "insert" ? "diff-insert" : o.type === "delete" ? "diff-delete" : ""}>{o.text}</span>)}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function VersionsTab({ m, onSaved }) {
  const { can } = useAuth();
  const [versions, setVersions] = useState([]);
  const [editing, setEditing] = useState(false);
  useEffect(() => { api.get(`/measures/${m.id}/versions`).then((r) => setVersions(r.data)); }, [m.id, m.row_version]);
  const cur = m.current_version;
  return (
    <div className="space-y-4">
      {editing ? <Editor m={m} current={cur} onClose={() => setEditing(false)} onSaved={onSaved} /> : (
        <Card title={cur ? `Current text — version ${cur.version_no}` : "Current text"} testId="current-version" actions={
          can("measure.edit") && !m.is_locked ? <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="new-version-button"><Plus className="mr-1.5 h-3.5 w-3.5" />New version</Button>
            : m.is_locked ? <span className="flex items-center gap-1 text-[11px] text-slate-500"><Lock className="h-3 w-3" />Immutable — amend via a new measure</span> : null}>
          {cur && (
            <article className="space-y-4 text-sm leading-relaxed text-slate-800">
              <h3 className="text-center text-base font-semibold uppercase">{cur.title}</h3>
              <p className="whitespace-pre-line">{cur.whereas_clauses}</p>
              <p className="whitespace-pre-line font-medium">{cur.enacting_clause}</p>
              <p className="whitespace-pre-line">{cur.body}</p>
              <div className="border-t pt-2 font-mono text-[10px] text-slate-400">SHA-256 {cur.content_hash}</div>
            </article>
          )}
        </Card>
      )}
      <Card title={`Revision history (${versions.length})`} testId="version-history">
        <ul className="divide-y divide-slate-100">
          {versions.map((v) => (
            <li key={v.id} className="flex items-center justify-between py-2 text-xs" data-testid={`version-row-${v.version_no}`}>
              <div>
                <span className="font-semibold">v{v.version_no}</span> — {v.change_summary}
                {v.is_current && <span className="ml-2 rounded bg-sky-100 px-1.5 text-[10px] text-sky-800">current</span>}
                {v.is_locked && <span className="ml-2 rounded bg-slate-100 px-1.5 text-[10px] text-slate-600">{v.locked_reason}</span>}
              </div>
              <div className="text-slate-500">{v.created_by_name} · {fmtDateTime(v.created_at)}</div>
            </li>
          ))}
        </ul>
      </Card>
      {versions.length > 1 && <Compare m={m} versions={versions} />}
    </div>
  );
}
